import type { Tour } from "@/components/ui/tour"
import { Bell, Bill, Book, Buildings2, Buildings3, Calendar, CalendarAdd, ChatSquare, ClockCircle, DollarMinimalistic, Help, Magnifer, Maximize, Notebook, Settings, SidebarMinimalistic, User, UsersGroupRounded, VolumeLoud, Wallet, Widget } from "@solar-icons/react"

export const SYSTEM_TOUR_ID = "system"
export const ADMIN_TOUR_ID = "admin-system"

export const systemTour: Tour = {
  id: SYSTEM_TOUR_ID,
  steps: [
    {
      id: "welcome",
      title: "Welcome",
      content:
        "This quick tour will show you the main areas of the app. Use the sidebar to move between sections.",
      icon: Book,
      side: "right",
      sideOffset: 12,
    },
    {
      id: "header-sidebar-trigger",
      title: "Sidebar menu",
      content: "Toggle the sidebar open or closed to show or hide the navigation menu.",
      icon: SidebarMinimalistic,
      side: "right",
      sideOffset: 12,
    },
    {
      id: "sidebar-business-switcher",
      title: "Business switcher",
      content:
        "Use the business menu next to the sidebar control in the header to switch businesses, open the full list, import from Excel, or add a business.",
      icon: Buildings2,
      side: "bottom",
      sideOffset: 12,
    },
    {
      id: "header-search",
      title: "Magnifer",
      content: "Press ⌘K (or Ctrl+K) to quickly jump to pages and actions across the app.",
      icon: Magnifer,
      side: "bottom",
      sideOffset: 12,
    },
    {
      id: "header-notifications",
      title: "Notifications",
      content: "View your latest notifications—appointments, payments, and reviews.",
      icon: Bell,
      side: "bottom",
      sideOffset: 12,
    },
    {
      id: "header-settings",
      title: "Settings",
      content: "Quick access to your account and app settings.",
      icon: Settings,
      side: "bottom",
      sideOffset: 12,
    },
    {
      id: "header-fullscreen",
      title: "Fullscreen",
      content: "Toggle fullscreen mode to focus on your work.",
      icon: Maximize,
      side: "bottom",
      sideOffset: 12,
    },
    {
      id: "header-profile",
      title: "Profile",
      content: "Open your profile, settings, help, and sign out from here.",
      icon: User,
      side: "bottom",
      sideOffset: 12,
    },
    {
      id: "dashboard",
      title: "Dashboard",
      content: "View your business overview, key metrics, and recent activity in one place.",
      icon: Widget,
      nextRoute: "/dashboard",
      nextLabel: "Go to Dashboard",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "appointments",
      title: "Appointments",
      content: "Manage bookings, view your calendar, and handle appointment statuses.",
      icon: Calendar,
      nextRoute: "/appointments",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "slot-requests",
      title: "Slot Requests",
      content: "Review and approve customer requests for additional appointment slots.",
      icon: CalendarAdd,
      nextRoute: "/slot-requests",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "customers",
      title: "Customers",
      content: "View customer profiles, booking history, and contact details.",
      icon: Notebook,
      nextRoute: "/customers",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "providers",
      title: "Service Providers",
      content: "Add and manage staff or providers who deliver your services.",
      icon: UsersGroupRounded,
      nextRoute: "/providers",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "services",
      title: "Services",
      content: "Create and manage the services you offer, set pricing, and configure availability.",
      icon: ClockCircle,
      nextRoute: "/services",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "finance",
      title: "Finance",
      content: "Track income, view transactions, manage payouts, and update bank details.",
      icon: DollarMinimalistic,
      nextRoute: "/finance",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "support",
      title: "Help & Support",
      content: "View customer issues and contact ForumOne for platform support.",
      icon: Help,
      nextRoute: "/support",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "ad-campaigns",
      title: "Ad Campaigns",
      content: "Create ad campaigns in a guided wizard: upload creative, pick a service and plan, then pay to submit for approval.",
      icon: VolumeLoud,
      nextRoute: "/ad-campaigns",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "business",
      title: "Businesses",
      content: "Manage multiple businesses, switch between them, or create new ones.",
      icon: Buildings2,
      nextRoute: "/business",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "settings",
      title: "Settings",
      content: "Update your profile, business profile, password, and other preferences.",
      icon: Settings,
      nextLabel: "Finish",
      side: "right",
      sideOffset: 12,
    },
  ],
}

export const adminTour: Tour = {
  id: ADMIN_TOUR_ID,
  steps: [
    {
      id: "admin-dashboard",
      title: "Welcome to Admin Dashboard",
      content: "This quick tour will show you the main administrative areas. Use the sidebar to move between sections.",
      icon: Book,
      side: "right",
      sideOffset: 12,
    },
    {
      id: "header-sidebar-trigger",
      title: "Sidebar menu",
      content: "Toggle the sidebar open or closed to show or hide the navigation menu.",
      icon: SidebarMinimalistic,
      side: "right",
      sideOffset: 12,
    },
    {
      id: "header-search",
      title: "Magnifer",
      content: "Quickly find businesses & institutions, admins, or transactions across the platform.",
      icon: Magnifer,
      side: "bottom",
      sideOffset: 12,
    },
    {
      id: "header-notifications",
      title: "Notifications",
      content: "Stay updated on pending approvals, new reports, and system alerts.",
      icon: Bell,
      side: "bottom",
      sideOffset: 12,
    },
    {
      id: "header-settings",
      title: "Quick Settings",
      content: "Access your account settings quickly from the header.",
      icon: Settings,
      side: "bottom",
      sideOffset: 12,
    },
    {
      id: "header-fullscreen",
      title: "Fullscreen",
      content: "Toggle fullscreen mode for a more focused viewing experience.",
      icon: Maximize,
      side: "bottom",
      sideOffset: 12,
    },
    {
      id: "header-profile",
      title: "Profile & Logout",
      content: "Manage your profile, change password, or sign out safely from here.",
      icon: User,
      side: "bottom",
      sideOffset: 12,
    },
    {
      id: "admin-businesses",
      title: "Manage Businesses & Institutions",
      content: "View, edit, and oversee all businesses and institutions registered on the platform.",
      icon: Buildings2,
      nextRoute: "/admin/businesses",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "admin-services",
      title: "Services",
      content:
        "Browse and manage services across all businesses and institutions—create, edit, and oversee offerings platform-wide.",
      icon: ClockCircle,
      nextRoute: "/admin/services",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "admin-providers",
      title: "Service Providers",
      content:
        "View and manage service providers across the platform, including creating providers and assigning them to businesses.",
      icon: UsersGroupRounded,
      nextRoute: "/admin/providers",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "admin-ad-campaigns",
      title: "Ad Campaigns",
      content:
        "Review and manage advertising campaigns submitted by businesses and institutions across the platform.",
      icon: VolumeLoud,
      nextRoute: "/admin/ad-campaigns",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "admin-appointments",
      title: "Appointments",
      content:
        "Monitor appointments across all businesses and institutions—view booking details, statuses, and activity.",
      icon: Calendar,
      nextRoute: "/admin/appointments",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "admin-approvals",
      title: "Pending Approvals",
      content: "Review and approve new sign-ups or business and institution changes.",
      icon: ClockCircle,
      nextRoute: "/admin/pending-approvals",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "admin-support",
      title: "Help & Support",
      content: "Review and resolve customer support tickets across disputes, payments, bookings, and general requests.",
      icon: Help,
      nextRoute: "/admin/support",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "admin-admins",
      title: "System Administrators",
      content: "Manage admin users, roles, and access for platform operations.",
      icon: UsersGroupRounded,
      nextRoute: "/admin/admins",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "admin-messaging",
      title: "Messaging",
      content: "Send announcements and manage communication across businesses and institutions.",
      icon: ChatSquare,
      nextRoute: "/admin/messaging",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "admin-transactions",
      title: "Transactions",
      content: "Monitor platform transactions and investigate payment activity.",
      icon: Bill,
      nextRoute: "/admin/transactions",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "admin-payouts",
      title: "Payout Requests",
      content: "Review and process business or institution payout requests.",
      icon: Wallet,
      nextRoute: "/admin/payouts",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "admin-ad-plans",
      title: "Ad Plans",
      content: "Configure advertising plans and pricing for businesses and institutions.",
      icon: VolumeLoud,
      nextRoute: "/admin/ad-plans",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "admin-industries",
      title: "Industries",
      content: "Manage the industry options available during business and institution onboarding.",
      icon: Buildings3,
      nextRoute: "/admin/industries",
      nextLabel: "Next",
      side: "right",
      sideOffset: 12,
    },
    {
      id: "admin-settings",
      title: "System Settings",
      content: "Configure global parameters, ad plans, and platform defaults.",
      icon: Settings,
      nextRoute: "/admin/settings/profile",
      nextLabel: "Finish",
      side: "right",
      sideOffset: 12,
    },
  ],
}

export const tours: Tour[] = [systemTour, adminTour]
